import * as _ from 'lodash';

import { AdjustmentOperationBasic, parseNumberOrThrow } from '../common';
import { InvalidAdjustmentOperationError } from '../errors';
import { BooleanBasic, parseAsBoolean } from '../common/boolean';

/**
 * Adjust a number by adding, subtracting, multiplying by, dividing by, or computing the modulo with another number.
 * @param firstOperand The number to adjust (left-hand operand).
 * @param operation The adjustment operation to perform.
 * @param secondOperand The adjustment amount (right-hand operand).
 *
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 * @throws {InvalidAdjustmentOperationError} "<operation>" is not a valid adjustment operation.
 */
export function adjustBasic<O extends `${AdjustmentOperationBasic}`>(
  firstOperand: number | string,
  operation: AdjustmentOperationBasic | O,
  secondOperand: number | string
): number {
  const firstOperandNumeric = parseNumberOrThrow(firstOperand, 'firstOperand');
  const secondOperandNumeric = parseNumberOrThrow(secondOperand, 'secondOperand');

  switch (operation) {
    case AdjustmentOperationBasic.adding:
      return firstOperandNumeric + secondOperandNumeric;
    case AdjustmentOperationBasic.subtracting:
      return firstOperandNumeric - secondOperandNumeric;
    case AdjustmentOperationBasic.multiplyingBy:
      return firstOperandNumeric * secondOperandNumeric;
    case AdjustmentOperationBasic.dividingBy:
      return firstOperandNumeric / secondOperandNumeric;
    case AdjustmentOperationBasic.modulo:
      return firstOperandNumeric % secondOperandNumeric;
  }

  throw new InvalidAdjustmentOperationError();
}

/**
 * Adjust a number rounding or flooring it.
 * @param num The number to adjust.
 * @param precision The precision to round to.
 *
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */

export function round(num: number | string, precision: number | string = 0): number {
  const valueNumeric = parseNumberOrThrow(num, 'number');
  const precisionNumeric = !precision ? 1 : parseNumberOrThrow(precision, 'precision');

  return _.round(valueNumeric, precisionNumeric);
}

/**
 * Adjust a number flooring it.
 * @param num The number to adjust.
 * @param precision The precision to floor to.
 *
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */

export function floor(num: number | string, precision: number | string = 0): number {
  const valueNumeric = parseNumberOrThrow(num, 'number');
  const precisionNumeric = !precision ? 1 : parseNumberOrThrow(precision, 'precision');

  return _.floor(valueNumeric, precisionNumeric);
}

/**
 * Adjust a number ceiling it.
 * @param num The number to adjust.
 * @param precision The precision to ceil to.
 *
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */

export function ceil(num: number | string, precision: number | string = 0): number {
  const valueNumeric = parseNumberOrThrow(num, 'number');
  const precisionNumeric = !precision ? 1 : parseNumberOrThrow(precision, 'precision');

  return _.ceil(valueNumeric, precisionNumeric);
}

/**
 * Compute the absolute value of a number.
 * @param num The number to adjust.
 *
 * @returns Absolute value of the number.
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */
export function abs(num: number | string): number {
  return Math.abs(parseNumberOrThrow(num, 'number'));
}

/**
 * Check if the first number is less than the second.
 * @param firstNumber The value to compare.
 * @param secondNumber The other value to compare.
 *
 * @returns True if the first number is less than the second. False otherwise.
 *
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */
export function lessThan(firstNumber: number | string, secondNumber: number | string): boolean {
  return _.lt(parseNumberOrThrow(firstNumber, 'firstNumber'), parseNumberOrThrow(secondNumber, 'secondNumber'));
}

/**
 * Check if the first number is less than or equal to the second.
 * @param firstNumber The value to compare.
 * @param secondNumber The other value to compare.
 *
 * @returns True if the first number is less than or equal the second. False otherwise.
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */
export function lessThanOrEqual(firstNumber: number | string, secondNumber: number | string): boolean {
  return _.lte(parseNumberOrThrow(firstNumber, 'firstNumber'), parseNumberOrThrow(secondNumber, 'secondNumber'));
}

/**
 * Check if the first number is greater than the second.
 * @param firstNumber The value to compare.
 * @param secondNumber The other value to compare.
 *
 * @returns True if the first number is greater than the second. False otherwise.
 *
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */
export function greaterThan(firstNumber: number | string, secondNumber: number | string): boolean {
  return _.gt(parseNumberOrThrow(firstNumber, 'firstNumber'), parseNumberOrThrow(secondNumber, 'secondNumber'));
}

/**
 * Check if the first number is greater than or equal to the second.
 * @param firstNumber The value to compare.
 * @param secondNumber The other value to compare.
 *
 * @returns True if the first number is greater than than or equal the second. False otherwise.
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */
export function greaterThanOrEqual(firstNumber: number | string, secondNumber: number | string): boolean {
  return _.gte(parseNumberOrThrow(firstNumber, 'firstNumber'), parseNumberOrThrow(secondNumber, 'secondNumber'));
}

/**
 * Check if a number is between two values, non-inclusive of the ending value.
 * @param number The number to check.
 * @param start The start of the range.
 * @param end The end of the range.
 *
 * @returns True if the number is within the specified range. False otherwise.
 *
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */
export function inRange(number: number | string, start: number | string, end: number | string): boolean {
  return _.inRange(
    parseNumberOrThrow(number, 'number'),
    parseNumberOrThrow(start, 'start'),
    parseNumberOrThrow(end, 'end')
  );
}

/**
 * Generate a random number between inclusive lower and upper bounds.
 * @param lower The lower bound.
 * @param upper The upper bound.
 * @param floating Whether to return a floating-point number.
 *
 * @returns The random number.
 *
 * @throws {InvalidNumberError} The value provided for "<property name>" is not a valid number.
 */
export function random(lower: number | string, upper: number | string, floating: boolean = true): number {
  if (lower && upper && floating) {
    return _.random(parseNumberOrThrow(lower, 'lower'), parseNumberOrThrow(upper, 'upper'), floating);
  } else if (lower && upper && !floating) {
    return _.random(parseNumberOrThrow(lower, 'lower'), parseNumberOrThrow(upper, 'upper'));
  } else if (upper && !lower && floating) {
    return _.random(parseNumberOrThrow(upper, 'upper'), floating);
  } else if (!lower && upper && !floating) {
    return _.random(parseNumberOrThrow(upper, 'upper'));
  }
  return _.random();
}

export function randomBasic<F extends `${BooleanBasic}`>(
  lower: number | string,
  upper: number | string,
  floating: BooleanBasic | F = BooleanBasic.yes
): number {
  return random(lower, upper, parseAsBoolean(floating));
}
